import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Menu, MenuButton, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import Logo from "@/assets/img/raro_digital_black.svg?react";
import { ROLE_ADMIN, useAuth } from "@/contexts/AuthContext";
import { ModuleNavigationProvider } from "@/contexts/ModuleNavigationContext";
import { useSidebarState } from "@/hooks/useSidebarState";
import {
  BASE_NAVIGATION,
  filterNavigationByRoles,
  shouldShowAdminMenu,
} from "./layout.definitions";
import {
  AdminMenu,
  NavigationSidebar,
  UserAvatar,
  UserDropdownMenu,
  GlobalCollaboratorSearch,
  NotificationsMenu
} from "./components";
import { ModuleNavigationBar } from "./components/ModuleNavigationBar";
import { checkPendingEvaluation } from "@/features/People/Evaluations/services/evaluationsService";

export default function Layout() {
  const location = useLocation();
  const { user, signOut, userHasRole } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebarState();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const showGlobalSearch = !!user;

  // Notification State
  const [pendingCampaignId, setPendingCampaignId] = useState<string | null>(null);

  // Filter navigation based on user roles
  const filteredNavigation = useMemo(() => {
    if (!user) return [];
    return filterNavigationByRoles(BASE_NAVIGATION, userHasRole);
  }, [user, userHasRole]);

  // Determine admin menu context
  const adminMenuContext = useMemo(
    () => shouldShowAdminMenu(location.pathname, userHasRole),
    [location.pathname, userHasRole]
  );

  useEffect(() => {
    const checkNotification = async () => {
      if (user?.collaboratorId) {
        const CACHE_KEY = `radar_notification_cache_${user.collaboratorId}`;
        const CACHE_TTL_MS = 60 * 1000; // 1 minute (reduced from 24h to reflect real-time updates)

        try {
          // 1. Try Cache
          const cachedRaw = localStorage.getItem(CACHE_KEY);
          if (cachedRaw) {
            const { id, timestamp } = JSON.parse(cachedRaw);
            const age = Date.now() - timestamp;

            if (age < CACHE_TTL_MS) {
              setPendingCampaignId(id);
              return; // Skip network call
            }
          }

          // 2. Fetch if stale or missing
          const id = await checkPendingEvaluation(user.collaboratorId);
          setPendingCampaignId(id);

          // 3. Save to Cache
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            id,
            timestamp: Date.now()
          }));

        } catch (e) {
          console.error(e);
        }
      }
    };
    checkNotification();
  }, [user?.collaboratorId]);


  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <ModuleNavigationProvider>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
        {/* Mobile Sidebar Overlay */}
        <Transition show={sidebarOpen} as={Fragment}>
          <div className="relative z-50 lg:hidden">
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 bg-raro-gray-900/80 backdrop-blur-sm"
                onClick={closeSidebar}
              />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <div className="relative mr-16 flex w-full max-w-xs flex-1">
                  <NavigationSidebar
                    navigation={filteredNavigation}
                    currentPath={location.pathname}
                    onNavigate={closeSidebar}
                    onClose={closeSidebar}
                    isMobile={true}
                  />
                </div>
              </Transition.Child>
            </div>
          </div>
        </Transition>

        {/* Desktop Sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${isCollapsed ? 'lg:w-20' : 'lg:w-72'
          }`}>
          <NavigationSidebar
            navigation={filteredNavigation}
            currentPath={location.pathname}
            isMobile={false}
            isCollapsed={isCollapsed}
          />
        </div>

        {/* Header */}
        <div className={`transition-all duration-300 ${isCollapsed ? 'lg:pl-20' : 'lg:pl-72'
          }`}>
          <div className="sticky top-0 z-40">
            <div className="relative flex h-16 shrink-0 items-center gap-x-4 border-b border-primary-200 bg-white/80 backdrop-blur-lg px-4 shadow-raro sm:gap-x-6 sm:px-6 lg:px-8">
              {/* Mobile menu button */}
              <button
                type="button"
                className="-m-2.5 p-2.5 text-raro-gray-700 lg:hidden hover:text-primary-600 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Abrir sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Desktop Logo + Collapse Button */}
              <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                <div className="hidden lg:flex lg:items-center lg:gap-x-4">
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="p-1.5 rounded-lg text-raro-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                    title={isCollapsed ? "Expandir menu" : "Recolher menu"}
                  >
                    <span className="sr-only">
                      {isCollapsed ? "Expandir menu" : "Recolher menu"}
                    </span>
                    {isCollapsed ? (
                      <ChevronRightIcon className="h-5 w-5" />
                    ) : (
                      <ChevronLeftIcon className="h-5 w-5" />
                    )}
                  </button>

                  <Link
                    to="/"
                    className="text-2xl font-bold text-raro-gray-900 font-montserrat hover:text-primary-600 transition-colors"
                  >
                    <Logo className="h-8 w-auto" />
                  </Link>
                </div>
              </div>

              {showGlobalSearch && (
                <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl justify-center px-4 z-50">
                  <div className="w-full">
                    <GlobalCollaboratorSearch />
                  </div>
                </div>
              )}

              {/* User Menu */}
              {user && (
                <div className="flex items-center gap-x-4 lg:gap-x-6">
                  {/* Notifications */}
                  <NotificationsMenu pendingCampaignId={pendingCampaignId} />

                  {/* Profile Dropdown */}
                  <Menu as="div" className="relative">
                    <MenuButton className="flex items-center p-1.5 rounded-full bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all hover:shadow-raro">
                      <span className="sr-only">Abrir menu do usuário</span>
                      <UserAvatar user={user} />
                    </MenuButton>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <UserDropdownMenu
                        user={user}
                        onSignOut={handleSignOut}
                      />
                    </Transition>
                  </Menu>
                </div>
              )}
            </div>

            {/* Module Navigation Bar */}
            <ModuleNavigationBar />
          </div>
        </div>

        {/* Main Content */}
        <div className={`transition-all duration-300 ${isCollapsed ? 'lg:pl-20' : 'lg:pl-72'
          }`}>
          <div className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex min-w-0">
                {/* Admin Sidebar */}
                {adminMenuContext.showAdminMenu && (
                  <div className="w-64 flex-shrink-0 pr-4">
                    <AdminMenu />
                  </div>
                )}

                {/* Page Content */}
                <div
                  className={`min-w-0 flex-1 ${adminMenuContext.showAdminMenu ? "lg:ml-8" : ""
                    }`}
                >
                  <div className="min-w-0 animate-fade-in">
                    <Outlet />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModuleNavigationProvider>
  );
}
